import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect, useSelector } from "react-redux";
import {
  Table,
  message,
  Button,
  Tag,
  Space,
  Modal,
  Input,
  Image,
  Descriptions,
  Tabs,
  Badge,
  Card,
  Form,
  Upload,
  Popconfirm,
  Select,
} from "antd";
import { SearchOutlined, PlusOutlined } from "@ant-design/icons";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { getMerchantSettings } from "../../utils/redux/reducers/Merchant";
import { ApiManager, Locale, Util } from "../../utils";
import {
  STORE_ATTRIBUTION_OPTIONS,
  attributionLabel,
} from "../../utils/api/StoreMembershipApi";
import { TIANDITU_ZOOM } from "../../constants";
import { createStaticMarkerMap } from "../../utils/tianditu";
import "./StoreMembershipList.scss";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();
const util = Util.getInstance();

function readAttributionFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    return params.get("attribution") || "";
  } catch (e) {
    return "";
  }
}

const STATUS_COLORS = {
  pending: "orange",
  approved: "green",
  rejected: "red",
  banned: "volcano",
};

const DETAIL_MAP_ID = "store-membership-detail-map";

const EMPTY_COUNTS = {
  all: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
  banned: 0,
};

function toFileList(url, path) {
  if (!url && !path) return [];
  return [
    {
      uid: "-1",
      name: "image",
      status: "done",
      url: url || undefined,
      response: path ? { path, url } : undefined,
    },
  ];
}

function pathFromFileList(fileList) {
  if (!fileList || !fileList.length) return "";
  const file = fileList[0];
  if (file?.response?.path) return file.response.path;
  if (file?.response?.url) return file.response.url;
  return file?.url || "";
}

function StoreMembershipList({ user }) {
  const settings = useSelector((state) => getMerchantSettings(state));
  const [list, setList] = useState({ data: [], current_page: 1, total: 0 });
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("pending");
  const [keyword, setKeyword] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [attribution, setAttribution] = useState(readAttributionFromUrl);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [banOpen, setBanOpen] = useState(false);
  const [banId, setBanId] = useState(null);
  const [banReason, setBanReason] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm] = Form.useForm();
  const [detail, setDetail] = useState(null);
  const mapInstance = useRef(null);

  const fetchList = useCallback(
    async (
      page = 1,
      statusFilter = status,
      keywordFilter = keyword,
      attributionFilter = attribution
    ) => {
      const { token } = user;
      if (!token) return;
      setLoading(true);
      try {
        const query = {
          page,
          limit: 20,
        };
        if (statusFilter) {
          query.status = statusFilter;
        }
        const trimmedKeyword = String(keywordFilter || "").trim();
        if (trimmedKeyword) {
          query.keyword = encodeURIComponent(trimmedKeyword);
        }
        if (attributionFilter) {
          query.attribution = attributionFilter;
        }
        const res = await api.storeMembership.list.run({
          token,
          query,
        });
        if (res.result) {
          setList(res.data);
          if (res.data?.status_counts) {
            setCounts({ ...EMPTY_COUNTS, ...res.data.status_counts });
          }
        } else {
          message.warning(res.message);
        }
      } catch (error) {
        message.error(error.message);
      } finally {
        setLoading(false);
      }
    },
    [user, status, keyword, attribution]
  );

  useEffect(() => {
    fetchList(1, status, keyword, attribution);
  }, [fetchList, status, keyword, attribution]);

  useEffect(() => {
    if (!detail) {
      mapInstance.current?.destroy?.();
      mapInstance.current = null;
      return undefined;
    }

    const lat = parseFloat(detail.latitude);
    const lng = parseFloat(detail.longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      createStaticMarkerMap({
        containerId: DETAIL_MAP_ID,
        longitude: lng,
        latitude: lat,
        zoom: TIANDITU_ZOOM,
      })
        .then((instance) => {
          if (cancelled) {
            instance.destroy?.();
            return;
          }
          mapInstance.current = instance;
          instance.resize?.();
        })
        .catch((error) => {
          console.log("StoreMembership detail map error", error);
        });
    }, 200);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      mapInstance.current?.destroy?.();
      mapInstance.current = null;
    };
  }, [detail]);

  const refreshCurrent = () => {
    fetchList(list.current_page || 1, status, keyword, attribution);
  };

  const onApprove = async (id) => {
    try {
      const res = await api.storeMembership.approve.run({
        token: user.token,
        path: { id },
      });
      if (res.result) {
        message.success(res.message || i18n.t("storeMembership.approved"));
        refreshCurrent();
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const onRejectConfirm = async () => {
    try {
      const res = await api.storeMembership.reject.run({
        token: user.token,
        path: { id: rejectId },
        data: { reject_reason: rejectReason },
      });
      if (res.result) {
        message.success(res.message || i18n.t("storeMembership.rejected"));
        setRejectOpen(false);
        setRejectId(null);
        setRejectReason("");
        refreshCurrent();
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const onBanConfirm = async () => {
    const reason = String(banReason || "").trim();
    if (!reason) {
      message.warning(i18n.t("storeMembership.banReasonRequired"));
      return;
    }
    try {
      const res = await api.storeMembership.ban.run({
        token: user.token,
        path: { id: banId },
        data: { ban_reason: reason },
      });
      if (res.result) {
        message.success(res.message || i18n.t("storeMembership.banned"));
        setBanOpen(false);
        setBanId(null);
        setBanReason("");
        refreshCurrent();
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    editForm.setFieldsValue({
      store_name: row.store_name || "",
      attribution: row.attribution || undefined,
      storefront: toFileList(row.storefront_image, row.storefront_image_path),
      license: toFileList(row.license_image, row.license_image_path),
    });
    setEditOpen(true);
  };

  const uploadImage = useCallback(
    async ({ file, onError, onSuccess }) => {
      const { token } = user;
      if (!token) return onError("no token");
      try {
        const res = await api.common.uploadImages.run({
          token,
          uploadFiles: [{ file }],
          uploadType: "merchant-info",
        });
        if (res.result) {
          onSuccess(res.data[0], file);
        } else {
          onError(res.message);
        }
      } catch (e) {
        onError(e);
      }
    },
    [user]
  );

  const onEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      const storefront = pathFromFileList(values.storefront);
      if (!storefront) {
        message.warning(i18n.t("storeMembership.storefrontRequired"));
        return;
      }
      setEditSaving(true);
      const res = await api.storeMembership.update.run({
        token: user.token,
        path: { id: editRow.id },
        data: {
          store_name: String(values.store_name || "").trim(),
          storefront_image: storefront,
          license_image: pathFromFileList(values.license) || null,
          attribution: values.attribution || null,
        },
      });
      if (res.result) {
        message.success(res.message || i18n.t("storeMembership.updated"));
        setEditOpen(false);
        setEditRow(null);
        editForm.resetFields();
        refreshCurrent();
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message);
    } finally {
      setEditSaving(false);
    }
  };

  const onDelete = async (id) => {
    try {
      const res = await api.storeMembership.destroy.run({
        token: user.token,
        path: { id },
      });
      if (res.result) {
        message.success(res.message || i18n.t("storeMembership.deleted"));
        if (detail?.id === id) setDetail(null);
        refreshCurrent();
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const onSearch = (value) => {
    setKeyword(String(value || "").trim());
  };

  const tabItems = useMemo(
    () => [
      {
        key: "pending",
        label: (
          <span className="store-membership-tab-label">
            {i18n.t("storeMembership.status_pending")}
            <Badge
              count={counts.pending}
              overflowCount={999}
              color="#fa8c16"
              className="store-membership-tab-badge"
            />
          </span>
        ),
      },
      {
        key: "approved",
        label: (
          <span className="store-membership-tab-label">
            {i18n.t("storeMembership.status_approved")}
            <Badge
              count={counts.approved}
              overflowCount={999}
              color="#52c41a"
              className="store-membership-tab-badge"
            />
          </span>
        ),
      },
      {
        key: "banned",
        label: (
          <span className="store-membership-tab-label">
            {i18n.t("storeMembership.status_banned")}
            <Badge
              count={counts.banned}
              overflowCount={999}
              color="#fa541c"
              className="store-membership-tab-badge"
            />
          </span>
        ),
      },
      {
        key: "rejected",
        label: (
          <span className="store-membership-tab-label">
            {i18n.t("storeMembership.status_rejected")}
            <Badge
              count={counts.rejected}
              overflowCount={999}
              color="#ff4d4f"
              className="store-membership-tab-badge"
            />
          </span>
        ),
      },
      {
        key: "",
        label: (
          <span className="store-membership-tab-label">
            {i18n.t("storeMembership.status_all")}
            <Badge
              count={counts.all}
              overflowCount={999}
              color="#8c8c8c"
              className="store-membership-tab-badge"
            />
          </span>
        ),
      },
    ],
    [counts]
  );

  if (!settings?.wholesale_store_verification) {
    return (
      <div className="store-membership-page">
        {i18n.t("storeMembership.enableHint")}
      </div>
    );
  }

  const columns = [
    {
      title: i18n.t("storeMembership.storeName"),
      dataIndex: "store_name",
      width: 160,
    },
    {
      title: i18n.t("storeMembership.user"),
      dataIndex: ["user", "mobile"],
      width: 160,
      render: (_, row) =>
        row.user
          ? `${row.user.nickname || ""} ${row.user.mobile || ""}`.trim()
          : row.user_id,
    },
    {
      title: i18n.t("storeMembership.address"),
      dataIndex: "address",
      ellipsis: true,
    },
    {
      title: i18n.t("storeMembership.attribution"),
      dataIndex: "attribution",
      width: 150,
      render: (value, row) =>
        row.attribution_label || attributionLabel(value) || (
          <span style={{ color: "#999" }}>
            {i18n.t("storeMembership.unassigned")}
          </span>
        ),
    },
    {
      title: i18n.t("storeMembership.storefront"),
      dataIndex: "storefront_image",
      width: 88,
      render: (url) =>
        url ? (
          <Image src={url} width={56} height={56} style={{ objectFit: "cover" }} />
        ) : (
          "-"
        ),
    },
    {
      title: i18n.t("storeMembership.license"),
      dataIndex: "license_image",
      width: 88,
      render: (url) =>
        url ? (
          <Image src={url} width={56} height={56} style={{ objectFit: "cover" }} />
        ) : (
          "-"
        ),
    },
    ...(status
      ? []
      : [
          {
            title: i18n.t("storeMembership.status"),
            dataIndex: "status",
            width: 100,
            render: (s) => (
              <Tag color={STATUS_COLORS[s] || "default"}>
                {i18n.t(`storeMembership.status_${s}`)}
              </Tag>
            ),
          },
        ]),
    {
      title: i18n.t("storeMembership.actions"),
      key: "actions",
      width: 320,
      fixed: "right",
      render: (_, row) => (
        <Space wrap size={8}>
          <Button size="small" onClick={() => setDetail(row)}>
            {i18n.t("storeMembership.show")}
          </Button>
          <Button size="small" onClick={() => openEdit(row)}>
            {i18n.t("storeMembership.edit")}
          </Button>
          {row.status === "pending" ? (
            <>
              <Button
                type="primary"
                size="small"
                onClick={() => onApprove(row.id)}
              >
                {i18n.t("storeMembership.approve")}
              </Button>
              <Button
                danger
                size="small"
                onClick={() => {
                  setRejectId(row.id);
                  setRejectOpen(true);
                }}
              >
                {i18n.t("storeMembership.reject")}
              </Button>
            </>
          ) : null}
          {row.status === "approved" ? (
            <Button
              danger
              size="small"
              onClick={() => {
                setBanId(row.id);
                setBanReason("");
                setBanOpen(true);
              }}
            >
              {i18n.t("storeMembership.ban")}
            </Button>
          ) : null}
          {row.status === "banned" ? (
            <Button
              type="primary"
              size="small"
              onClick={() => onApprove(row.id)}
            >
              {i18n.t("storeMembership.unban")}
            </Button>
          ) : null}
          <Popconfirm
            title={i18n.t("storeMembership.deleteConfirm")}
            onConfirm={() => onDelete(row.id)}
            okText={i18n.t("storeMembership.delete")}
            cancelText={i18n.t("storeMembership.close")}
          >
            <Button danger type="link" size="small">
              {i18n.t("storeMembership.delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const hasMap =
    detail &&
    !Number.isNaN(parseFloat(detail.latitude)) &&
    !Number.isNaN(parseFloat(detail.longitude));

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{i18n.t("storeMembership.upload")}</div>
    </div>
  );

  return (
    <div className="store-membership-page">
      <Card bordered={false} className="store-membership-card">
        <div className="store-membership-toolbar">
          <Tabs
            className="store-membership-tabs"
            activeKey={status}
            onChange={(key) => setStatus(key)}
            items={tabItems}
          />
          <Space wrap>
            <Select
              allowClear
              style={{ minWidth: 180 }}
              placeholder={i18n.t("storeMembership.attributionAll")}
              value={attribution || undefined}
              onChange={(value) => setAttribution(value || "")}
              options={[
                ...STORE_ATTRIBUTION_OPTIONS,
                {
                  value: "unassigned",
                  label: i18n.t("storeMembership.unassigned"),
                },
              ]}
            />
            <Input.Search
              allowClear
              className="store-membership-search"
              placeholder={i18n.t("storeMembership.searchPlaceholder")}
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={onSearch}
              enterButton={i18n.t("storeMembership.search")}
            />
          </Space>
        </div>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={list.data || []}
          scroll={{ x: 1250 }}
          pagination={{
            current: list.current_page || 1,
            total: list.total || 0,
            pageSize: list.per_page || 20,
            showSizeChanger: false,
            showTotal: (total) =>
              i18n.t("storeMembership.totalCount", { count: total }),
            onChange: (page) =>
              fetchList(page, status, keyword, attribution),
          }}
        />
      </Card>

      <Modal
        title={i18n.t("storeMembership.reject")}
        open={rejectOpen}
        onOk={onRejectConfirm}
        onCancel={() => setRejectOpen(false)}
      >
        <Input.TextArea
          rows={3}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder={i18n.t("storeMembership.rejectReason")}
        />
      </Modal>

      <Modal
        title={i18n.t("storeMembership.ban")}
        open={banOpen}
        onOk={onBanConfirm}
        onCancel={() => {
          setBanOpen(false);
          setBanId(null);
          setBanReason("");
        }}
        okButtonProps={{ danger: true }}
      >
        <Input.TextArea
          rows={3}
          value={banReason}
          onChange={(e) => setBanReason(e.target.value)}
          placeholder={i18n.t("storeMembership.banReason")}
        />
      </Modal>

      <Modal
        title={i18n.t("storeMembership.edit")}
        open={editOpen}
        onOk={onEditSave}
        confirmLoading={editSaving}
        onCancel={() => {
          setEditOpen(false);
          setEditRow(null);
          editForm.resetFields();
        }}
        destroyOnClose
        width={520}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="store_name"
            label={i18n.t("storeMembership.storeName")}
            rules={[
              {
                required: true,
                message: i18n.t("storeMembership.storeNameRequired"),
              },
            ]}
          >
            <Input maxLength={191} />
          </Form.Item>
          <Form.Item
            name="attribution"
            label={i18n.t("storeMembership.attribution")}
          >
            <Select
              allowClear
              placeholder={i18n.t("storeMembership.unassigned")}
              options={STORE_ATTRIBUTION_OPTIONS}
            />
          </Form.Item>
          <Form.Item
            name="storefront"
            label={i18n.t("storeMembership.storefront")}
            valuePropName="fileList"
            getValueFromEvent={(e) => util.normFile(e)}
            rules={[
              {
                required: true,
                message: i18n.t("storeMembership.storefrontRequired"),
              },
            ]}
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              accept="image/*"
              customRequest={uploadImage}
            >
              {uploadButton}
            </Upload>
          </Form.Item>
          <Form.Item
            name="license"
            label={i18n.t("storeMembership.license")}
            valuePropName="fileList"
            getValueFromEvent={(e) => util.normFile(e)}
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              accept="image/*"
              customRequest={uploadImage}
            >
              {uploadButton}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={i18n.t("storeMembership.detailTitle")}
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={
          <Button onClick={() => setDetail(null)}>
            {i18n.t("storeMembership.close")}
          </Button>
        }
        width={720}
        destroyOnClose
      >
        {detail ? (
          <>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={i18n.t("storeMembership.storeName")}>
                {detail.store_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label={i18n.t("storeMembership.status")}>
                <Tag color={STATUS_COLORS[detail.status] || "default"}>
                  {i18n.t(`storeMembership.status_${detail.status}`)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={i18n.t("storeMembership.address")}>
                {detail.address || "-"}
              </Descriptions.Item>
              <Descriptions.Item label={i18n.t("storeMembership.attribution")}>
                {detail.attribution_label ||
                  attributionLabel(detail.attribution) ||
                  i18n.t("storeMembership.unassigned")}
              </Descriptions.Item>
              {detail.reject_reason ? (
                <Descriptions.Item
                  label={
                    detail.status === "banned"
                      ? i18n.t("storeMembership.banReason")
                      : i18n.t("storeMembership.rejectReason")
                  }
                >
                  {detail.reject_reason}
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label={i18n.t("storeMembership.storefront")}>
                {detail.storefront_image ? (
                  <Image
                    src={detail.storefront_image}
                    width={160}
                    style={{ objectFit: "cover" }}
                  />
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label={i18n.t("storeMembership.license")}>
                {detail.license_image ? (
                  <Image
                    src={detail.license_image}
                    width={160}
                    style={{ objectFit: "cover" }}
                  />
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label={i18n.t("storeMembership.location")}>
                {hasMap
                  ? `${detail.latitude}, ${detail.longitude}`
                  : i18n.t("storeMembership.noLocation")}
              </Descriptions.Item>
            </Descriptions>
            {hasMap ? (
              <div
                id={DETAIL_MAP_ID}
                style={{
                  width: "100%",
                  height: 320,
                  marginTop: 16,
                  borderRadius: 8,
                  overflow: "hidden",
                  border: "1px solid #f0f0f0",
                }}
              />
            ) : null}
          </>
        ) : null}
      </Modal>
    </div>
  );
}

export default connect((state) => ({
  user: getUserInfo(state),
}))(StoreMembershipList);
